import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { RouterType, useRouter } from 'shared-routing';
import { setCurrentStepByHash } from '../../helpers/setCurrentStepByHash';
import { FormStep, StepTypeWithIndex } from '../../types';

const writeStepHash = (router: RouterType, method: 'push' | 'replace', step: string) => {
  const [asPathWithoutHash] = router.asPath.split('#');
  return router[method](
    { pathname: router.pathname, query: router.query },
    `${asPathWithoutHash}#${step}`,
    { shallow: true, scroll: false }
  );
};

export const useStepHistory = (
  currentStep: StepTypeWithIndex,
  setCurrentStep: Dispatch<SetStateAction<StepTypeWithIndex>>,
  allFormSteps: FormStep[]
) => {
  const router = useRouter();
  const visitedSteps = useRef(new Set<string>());
  const latest = useRef({ router, currentStep, allFormSteps });
  latest.current = { router, currentStep, allFormSteps };

  useEffect(() => {
    const isEnteringWizard = visitedSteps.current.size === 0;
    allFormSteps
      .slice(currentStep.index + 1)
      .forEach(({ step }) => visitedSteps.current.delete(step));
    visitedSteps.current.add(currentStep.step);

    if (window.location.hash === `#${currentStep.step}`) {
      return;
    }

    writeStepHash(router, isEnteringWizard ? 'replace' : 'push', currentStep.step);
  }, [currentStep.step]);

  useEffect(() => {
    const handleHashChange = () => {
      const { router, currentStep, allFormSteps } = latest.current;
      const step = window.location.hash.slice(1);

      if (step === currentStep.step || !allFormSteps.some((opt) => opt.step === step)) {
        return;
      }

      if (!visitedSteps.current.has(step)) {
        router.back();
        return;
      }

      setCurrentStepByHash(window.location.hash, allFormSteps, setCurrentStep);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);
};
